import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { SupportedAiModelResponse } from "../../../../@api";

import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";
import { useAiModelsService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";

import "./ImageAiModelControl.scss";

interface IImageModelControlProps {
  imageAiModel: SupportedAiModelResponse | undefined;
  disabled: boolean;
  onChange: (imageAiModel: SupportedAiModelResponse) => void;
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const controlTranslationKeys = featureTranslationKeys.aiModelControl;

type ModelOption = {
  label: string;
  value: string; // modelId
  model: SupportedAiModelResponse;
};

export const ImageAiModelControl: FunctionComponent<IImageModelControlProps> = ({
  imageAiModel,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();

  const [isImageAiModelValid, setIsImageAiModelValid] = useState(true);
  const [imageAiModelOptions, setImageAiModelOptions] = useState<SupportedAiModelResponse[]>([]);
  const [loadingAiModels, setLoadingAiModels] = useState(false);

  const { globalErrorsStore } = useStore();
  const aiModelsService = useAiModelsService();

  useEffect(() => {
    const requestImageModels = async () => {
      setLoadingAiModels(true);

      try {
        const response = await aiModelsService.aiModelsGetSupportedImageModels();

        setImageAiModelOptions(Array.isArray(response) ? response : []);
        globalErrorsStore.removeError(featureTranslationKeys.errors.invalidAiModel);
      } catch (error: any) {
        globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidAiModel);
        console.error("[ImageAiModelControl] Failed to load a list of AiModels", error);
      } finally {
        setLoadingAiModels(false);
      }
    };

    if (!imageAiModelOptions?.length) {
      requestImageModels();
    }
  }, [aiModelsService, imageAiModelOptions?.length]);

  const dropdownOptions: ModelOption[] = useMemo(() => {
    return (imageAiModelOptions ?? []).map((aiModel: SupportedAiModelResponse) => {
      const key = `feature.imageGeneration.aiModelControl.model.${aiModel.modelName}`;

      return {
        value: String(aiModel.modelId),
        label: t(key) || aiModel.modelName, // fallback to modelName when translation is missing
        model: aiModel,
      };
    });
  }, [imageAiModelOptions, t]);

  // default to the first item once options are loaded
  useEffect(() => {
    if (!imageAiModel && imageAiModelOptions.length > 0) {
      onChange(imageAiModelOptions[0]);
      setIsImageAiModelValid(true);
      globalErrorsStore.removeError(featureTranslationKeys.errors.invalidAiModel);
    }
  }, [imageAiModel, imageAiModelOptions, globalErrorsStore]);

  const selectedValue = useMemo(() => {
    const id = imageAiModel?.modelId;
    return id ? String(id) : null;
  }, [imageAiModel]);

  const handleImageAiModelChange = (e: DropdownChangeEvent) => {
    const modelId = (e.value as string | null) ?? null;

    const option = dropdownOptions.find(o => o.value === modelId) ?? null;
    const nextModel = option?.model;

    const valid = !!nextModel;
    setIsImageAiModelValid(valid);

    if (!valid) {
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidAiModel);
    } else {
      onChange(nextModel);
      globalErrorsStore.removeError(featureTranslationKeys.errors.invalidAiModel);
    }
  };

  const tooltipKey = isImageAiModelValid
    ? controlTranslationKeys.aiModelTooltip
    : featureTranslationKeys.errors.invalidAiModel;

  return (
    <div className={classNames({ "image-ai-model-dropdown-with-icon": loadingAiModels })}>
      <TooltipWrapper
        targetRef=".image-ai-model"
        position="bottom"
        content={t(tooltipKey)}
        disabled={disabled || loadingAiModels}
      />

      {loadingAiModels && (
        <span className="left-icon" aria-hidden="true">
          <ProgressSpinner style={{ width: "1rem", height: "1rem" }} />
        </span>
      )}

      <Dropdown
        data-testid="image-ai-model-dropdown"
        invalid={!isImageAiModelValid}
        className={classNames("image-ai-model", { error: !isImageAiModelValid })}
        value={selectedValue}
        options={dropdownOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleImageAiModelChange}
        disabled={disabled || loadingAiModels}
        showClear={false}
        placeholder={t(controlTranslationKeys.aiModelLabel)}
        aria-label={t(controlTranslationKeys.aiModelTooltip)}
      />
    </div>
  );
};
